import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tradingService } from '../../services/tradingService';
import StrategyCard from './StrategyCard';
import BacktestMetricsPanel from './BacktestMetricsPanel';

/**
 * Modal overlay that shows the full picture for a single deployed strategy —
 * the same StrategyCard used in the Strategies tab PLUS the deep backtest /
 * optimization metrics panel. Opened by clicking a portfolio member row.
 *
 * Self-contained for display data (strategy, backtest, signals, combo,
 * indicators, audit). Mutating actions are delegated to the parent via
 * callbacks so list state stays consistent; after a mutation we re-fetch
 * our own snapshot.
 *
 * Props:
 *   strategyId     id to load
 *   pendingSlices  pending order slices for this strategy (optional)
 *   onClose()      close the modal
 *   onToggleActive(strategy)  onDelete(strategy)  onWithdraw(strategy)  onCancelStop(strategy)
 *   onSaveAlloc(strategy, value)   (all optional; async-aware)
 *   onMutated()    notify parent to reload its list (optional)
 */
const StrategyDetailModal = ({
  strategyId,
  portfolioId = null,
  pendingSlices = [],
  onClose,
  onToggleActive,
  onDelete,
  onWithdraw,
  onCancelStop,
  onSaveAlloc,
  onMutated,
}) => {
  const navigate = useNavigate();
  const [strategy, setStrategy]   = useState(null);
  const [bt, setBt]               = useState(null);
  const [sigs, setSigs]           = useState([]);
  const [combo, setCombo]         = useState(null);
  const [runMeta, setRunMeta]     = useState(null);
  const [member, setMember]       = useState(null);   // portfolio track-record for this strategy
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [indicators, setIndicators]               = useState(undefined);
  const [indicatorsLoading, setIndicatorsLoading] = useState(false);
  const [auditLoading, setAuditLoading]           = useState(false);

  const fetchStrategy = useCallback(() => {
    if (!strategyId) return;
    return tradingService.getStrategy(strategyId).then(r => setStrategy(r.data));
  }, [strategyId]);

  // Initial load: strategy + backtest + (if active) live signals + (if from an
  // optimization) the matched combo for blended/OOS metrics.
  useEffect(() => {
    let cancelled = false;
    if (!strategyId) return;
    setLoading(true);
    setError(null);
    setBt(null); setSigs([]); setCombo(null); setRunMeta(null);
    setMember(null); setIndicators(undefined);

    tradingService.getStrategy(strategyId)
      .then(r => {
        if (cancelled) return;
        const s = r.data;
        setStrategy(s);

        tradingService.getBacktests(strategyId)
          .then(br => { if (!cancelled) setBt((br.data || [])[0] || null); })
          .catch(() => {});

        if (s.is_active) {
          tradingService.getLiveSignals(strategyId)
            .then(sr => { if (!cancelled) setSigs(sr.data || []); })
            .catch(() => {});
        }

        // Optimization combo: blended/IS/OOS metrics + B&H benchmarks. The
        // dedicated endpoint resolves the single combo across all storage
        // modes (RDS / S3) — getOptimizationStatus does NOT return combos.
        if (s.source_opt_run_id != null && s.source_combo_index != null) {
          tradingService.getOptimizationCombo(s.source_opt_run_id, s.source_combo_index)
            .then(or => {
              if (cancelled) return;
              setCombo(or.data?.combo || null);
              setRunMeta(or.data?.run_meta || null);
            })
            .catch(() => {});
        }

        // Portfolio track record: per-member regenerated backtest (metrics +
        // equity curve + same-window SPX). Only when opened from a portfolio.
        if (portfolioId != null) {
          tradingService.getPortfolioMemberAnalytics(portfolioId, strategyId)
            .then(mr => {
              if (cancelled) return;
              setMember(mr.data?.status === 'complete' ? mr.data : null);
            })
            .catch(() => {});
        }
      })
      .catch(e => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [strategyId, portfolioId]);

  // Indicators — fetched on demand (display-only, owned locally).
  const handleCheckIndicators = useCallback(() => {
    setIndicatorsLoading(true);
    tradingService.getLiveIndicators(strategyId)
      .then(r => setIndicators(r.data))
      .catch(e => setIndicators({ error: e?.response?.data?.error || 'Failed to load indicators' }))
      .finally(() => setIndicatorsLoading(false));
  }, [strategyId]);

  // Audit Excel — display concern, owned locally for the spinner.
  const handleDownloadAudit = useCallback(async () => {
    if (!strategy) return;
    setAuditLoading(true);
    try {
      const resp = await tradingService.downloadAuditExcel(strategy.id, {});
      const url  = URL.createObjectURL(new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(strategy.symbols || [''])[0]}_${strategy.name.slice(0, 35).replace(/\s+/g, '_')}_audit.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Audit export failed: ' + (e?.response?.data ? 'Server error — check logs' : e.message));
    } finally {
      setAuditLoading(false);
    }
  }, [strategy]);

  // Mutations delegate to parent, then refresh our snapshot + the list.
  const afterMutate = useCallback(async () => {
    await fetchStrategy();
    onMutated && onMutated();
  }, [fetchStrategy, onMutated]);

  const handleToggle = useCallback(async () => {
    if (!strategy || !onToggleActive) return;
    await onToggleActive(strategy);
    afterMutate();
  }, [strategy, onToggleActive, afterMutate]);

  const handleDelete = useCallback(async () => {
    if (!strategy || !onDelete) return;
    await onDelete(strategy);   // parent confirms + deletes + reloads list
    onClose && onClose();
  }, [strategy, onDelete, onClose]);

  const handleWithdraw = useCallback(async () => {
    if (!strategy || !onWithdraw) return;
    await onWithdraw(strategy);
    afterMutate();
  }, [strategy, onWithdraw, afterMutate]);

  const handleCancelStop = useCallback(async () => {
    if (!strategy || !onCancelStop) return;
    await onCancelStop(strategy);
    afterMutate();
  }, [strategy, onCancelStop, afterMutate]);

  const handleSaveAlloc = useCallback(async (val) => {
    if (!strategy || !onSaveAlloc) return;
    await onSaveAlloc(strategy, val);
    afterMutate();
  }, [strategy, onSaveAlloc, afterMutate]);

  const handleGoBacktest = useCallback(() => {
    if (!strategy) return;
    navigate(`/trading/backtest?strategy=${strategy.strategy_class}&symbol=${(strategy.symbols || [])[0] || ''}&strategy_id=${strategy.id}`);
  }, [strategy, navigate]);

  const idleAsset = strategy && strategy.idle_deployment && strategy.idle_deployment !== 'cash'
    ? strategy.idle_deployment : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center bg-black/60 backdrop-blur-sm p-3 md:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl my-auto bg-white dark:bg-[#0d1117] rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[92vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 dark:border-gray-800 shrink-0">
          <h2 className="font-serif text-base md:text-lg text-gray-900 dark:text-gray-100 truncate pr-4">
            {strategy ? strategy.name : 'Strategy'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl leading-none shrink-0"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto scrollbar-thin p-4 md:p-5 space-y-5">
          {loading && <p className="text-sm text-gray-500 animate-pulse">Loading strategy…</p>}
          {error && <p className="text-sm text-red-400">{error}</p>}

          {strategy && (
            <>
              <StrategyCard
                s={strategy}
                bt={bt}
                sigs={sigs}
                indicators={indicators}
                indicatorsLoading={indicatorsLoading}
                auditLoading={auditLoading}
                pendingSlices={pendingSlices}
                cardCls="bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5"
                onToggleActive={handleToggle}
                onDelete={handleDelete}
                onWithdraw={handleWithdraw}
                onCancelStop={onCancelStop ? handleCancelStop : undefined}
                onSaveAlloc={handleSaveAlloc}
                onGoBacktest={handleGoBacktest}
                onDownloadAudit={handleDownloadAudit}
                onCheckIndicators={handleCheckIndicators}
              />

              <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
                <BacktestMetricsPanel
                  bt={bt}
                  combo={combo}
                  runMeta={runMeta}
                  member={member}
                  idleAsset={idleAsset}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StrategyDetailModal;
